import { initContract } from "@ts-rest/core";
import { z } from "zod";

const c = initContract();

const RegisterUserRequestBody = z
  .object({
    firstname: z.string(),
    lastname: z.string(),
    middlename: z.string().optional(),
    id: z.string().optional(),
    username: z
      .string()
      .min(3)
      .max(50)
      .regex(/^[a-zA-Z0-9_.-]*$/),
    email: z
      .string()
      .max(254)
      .regex(
        /^[\w.!#$%&'*+\/=?^`{|}~-]+@[a-zA-Z\d](?:[a-zA-Z\d-]{0,61}[a-zA-Z\d])?(?:\.[a-zA-Z\d](?:[a-zA-Z\d-]{0,61}[a-zA-Z\d])?)*$/,
      ),
    password: z.string(),
    country: z.string(),
    file: z.string().optional(),
  })
  .passthrough();
const User = z
  .object({
    firstname: z.string(),
    lastname: z.string(),
    middlename: z.string().optional(),
    _id: z.string(),
    id: z.string(),
    username: z.string(),
    email: z.string(),
    userType: z.string(),
    country: z.string(),
    files: z.array(z.string()),
    createdAt: z.string().datetime({ offset: true }).optional(),
    loginFailedAt: z.string().datetime({ offset: true }).optional(),
    loginSucceededAt: z.string().datetime({ offset: true }).optional(),
    passwordChangedAt: z.string().datetime({ offset: true }).optional(),
    attackLibraryDownloadedAt: z.string().datetime({ offset: true }).optional(),
  })
  .passthrough();
const File = z
  .object({
    fileName: z.string().max(255),
    _id: z.string().optional(),
    id: z.string().optional(),
    fileSize: z.number().int(),
    fileType: z.string(),
    sha256: z.string().optional(),
    forceUpdate: z.boolean().optional(),
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
  })
  .passthrough();
const FileWithUsage = File.and(
  z.object({ assignmentCount: z.number().int() }).passthrough(),
);
const FilePatch = z
  .object({
    fileName: z.string().max(255),
    forceUpdate: z.boolean().optional(),
  })
  .passthrough();
const UpdatesSummary = z
  .object({
    app: z.union([
      z.object({}).strict(),
      z
        .object({
          versionName: z.string(),
          versionCode: z.number().int(),
          forceUpdate: z.boolean(),
          downloadUrl: z.string(),
          releaseNotes: z.string(),
          sha256: z.string(),
        })
        .passthrough(),
    ]),
    attackLibrary: z.union([
      z.object({}).strict(),
      z
        .object({
          fileName: z.string(),
          sha256: z.string(),
          fileSize: z.number().int(),
          forceUpdate: z.boolean(),
          downloadUrl: z.literal("/api/attack-library"),
        })
        .passthrough(),
    ]),
    firmware: z.union([
      z.object({}).strict(),
      z
        .object({
          versionName: z.string(),
          versionCode: z.number().int(),
          forceUpdate: z.boolean(),
          fileName: z.string(),
          sha256: z.string(),
          fileSize: z.number().int(),
          downloadUrl: z.literal("/api/updates/download-firmware"),
          releaseNotes: z.string(),
        })
        .passthrough(),
    ]),
  })
  .passthrough();
const ProblemDetails = z
  .object({
    type: z.string().url(),
    title: z.string(),
    status: z.number().int().gte(100).lte(599),
    detail: z.string().optional(),
    instance: z.string().url().optional(),
    errors: z
      .array(
        z
          .object({
            detail: z.string().optional(),
            pointer: z.string().optional(),
            parameter: z.string().optional(),
          })
          .passthrough(),
      )
      .optional(),
    message: z.string().optional(),
    error: z.string().optional(),
  })
  .passthrough();
const UserType = z.enum(["Admin", "User"]);
const LoginUserResponse = z
  .object({
    data: z
      .object({
        token: z.string(),
        user: z
          .object({
            id: z.string(),
            name: z.string(),
            email: z.string(),
            userType: UserType,
          })
          .passthrough(),
      })
      .passthrough(),
  })
  .passthrough();

export const schemas = {
  RegisterUserRequestBody,
  User,
  File,
  FileWithUsage,
  FilePatch,
  UpdatesSummary,
  ProblemDetails,
  UserType,
  LoginUserResponse,
};

export const contract = c.router({
  apiAttackLibrary: {
    method: "GET",
    path: "/api/attack-library",
    summary: "Authenticate and download assigned attack library file",
    responses: {
      200: c.otherResponse({
        contentType: "application/octet-stream",
        body: z.custom<File>(),
      }),
      401: ProblemDetails,
      404: ProblemDetails,
      500: ProblemDetails,
    },
  },
  "~apiUpdatesSummary": {
    method: "GET",
    path: "/api/updates/summary",
    summary: "Get latest app and attack library update summary",
    query: z.object({
      versionCode: z.number().int().gte(0).optional(),
      firmwareVersionName: z.string().optional(),
      firmwareVersionCode: z.number().int().gte(0).optional(),
    }),
    responses: {
      200: UpdatesSummary,
      401: z.object({ error: z.string().optional() }).passthrough(),
      500: ProblemDetails,
    },
  },
  apiFileUpload: {
    method: "POST",
    path: "/api/file-upload",
    summary: "Upload a file (admin only)",
    body: z.object({ file: z.custom<File>().optional() }).passthrough(),
    contentType: "multipart/form-data",
    responses: {
      200: z
        .object({ message: z.string(), file: File.optional() })
        .passthrough(),
      400: ProblemDetails,
      401: ProblemDetails,
      403: ProblemDetails,
      500: ProblemDetails,
    },
  },
  "~apiUploadDeprecated": {
    method: "POST",
    path: "/api/upload",
    summary: "Upload a file for a user",
    body: z
      .object({
        file: z.custom<File>().optional(),
        userId: z.string().optional(),
      })
      .passthrough(),
    contentType: "multipart/form-data",
    responses: { 410: ProblemDetails },
  },
  apiFiles: {
    method: "GET",
    path: "/api/files",
    summary: "Get all uploaded files",
    query: z.object({
      search: z.string().optional(),
      sort: z
        .enum([
          "fileName_asc",
          "fileName_desc",
          "fileSize_asc",
          "fileSize_desc",
          "fileType_asc",
          "fileType_desc",
          "createdAt_asc",
          "createdAt_desc",
          "updatedAt_asc",
          "updatedAt_desc",
          "assignmentCount_asc",
          "assignmentCount_desc",
        ])
        .optional(),
      limit: z.string().optional(),
      offset: z.string().optional(),
    }),
    responses: {
      200: z
        .object({
          data: z.array(FileWithUsage),
          metadata: z
            .object({ totalCount: z.number().int().gte(0) })
            .passthrough(),
        })
        .passthrough(),
      400: ProblemDetails,
      401: ProblemDetails,
      403: ProblemDetails,
      405: ProblemDetails,
      500: ProblemDetails,
    },
  },
  deleteFileByFileId: {
    method: "DELETE",
    path: "/api/files/:fileId",
    summary: "Delete a file",
    pathParams: z.object({ fileId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z
        .object({
          status: z.literal("ok").optional(),
          data: z.literal("Deleted").optional(),
        })
        .passthrough(),
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      500: ProblemDetails,
    },
  },
  updateFileByFileId: {
    method: "PATCH",
    path: "/api/files/:fileId",
    summary: "Update a file's metadata",
    pathParams: z.object({ fileId: z.string() }),
    body: FilePatch,
    contentType: "application/json",
    responses: {
      200: File,
      400: ProblemDetails,
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      500: ProblemDetails,
    },
  },
  apiUsersUserIdFiles: {
    method: "GET",
    path: "/api/users/:userId/files",
    summary: "Get files for a specific user",
    query: z.object({ search: z.string().optional() }),
    pathParams: z.object({ userId: z.string() }),
    responses: {
      200: z.object({ files: z.array(File) }).passthrough(),
      400: ProblemDetails,
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      500: ProblemDetails,
    },
  },
  unassignFile: {
    method: "DELETE",
    path: "/api/users/:userId/files/:fileId",
    summary: "Unassign a file from a user",
    pathParams: z.object({ userId: z.string(), fileId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z
        .object({
          status: z.literal("ok").optional(),
          data: z.literal("Unassigned").optional(),
        })
        .passthrough(),
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      500: ProblemDetails,
    },
  },
  apiAssignFile: {
    method: "POST",
    path: "/api/assign-file",
    summary: "Assign a file to a user",
    body: z.object({ userId: z.string(), fileId: z.string() }).passthrough(),
    contentType: "application/json",
    responses: {
      200: z.object({ message: z.string() }).passthrough(),
      400: ProblemDetails,
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      500: ProblemDetails,
    },
  },
  "~getAllUsersDeprecated": {
    method: "POST",
    path: "/getAllUsers",
    summary: "Register a new user",
    body: c.noBody(),
    responses: { 410: ProblemDetails },
  },
  "~apiLoginDeprecated": {
    method: "POST",
    path: "/api/login",
    summary: "Register a new user",
    body: c.noBody(),
    responses: { 410: ProblemDetails },
  },
  registerUser: {
    method: "POST",
    path: "/api/users",
    summary: "Create a standard user account",
    body: RegisterUserRequestBody,
    contentType: "application/json",
    responses: {
      200: z.object({ status: z.literal("ok") }).passthrough(),
      400: ProblemDetails,
      401: ProblemDetails,
      403: ProblemDetails,
      409: ProblemDetails,
      500: ProblemDetails,
    },
  },
  getAllUsers: {
    method: "GET",
    path: "/api/users",
    summary: "Get all users",
    query: z.object({
      search: z.string().optional(),
      sort: z
        .enum([
          "attackLibraryDownloadedAt_asc",
          "attackLibraryDownloadedAt_desc",
          "country_asc",
          "country_desc",
          "createdAt_asc",
          "createdAt_desc",
          "email_asc",
          "email_desc",
          "files_asc",
          "files_desc",
          "firstname_asc",
          "firstname_desc",
          "lastname_asc",
          "lastname_desc",
          "loginFailedAt_asc",
          "loginFailedAt_desc",
          "loginSucceededAt_asc",
          "loginSucceededAt_desc",
          "middlename_asc",
          "middlename_desc",
          "passwordChangedAt_asc",
          "passwordChangedAt_desc",
          "username_asc",
          "username_desc",
          "userType_asc",
          "userType_desc",
        ])
        .optional(),
      limit: z.string().optional(),
      offset: z.string().optional(),
      filename: z.string().min(1).max(255).optional(),
    }),
    responses: {
      200: z
        .object({
          status: z.literal("ok"),
          metadata: z
            .object({ totalCount: z.number().int().gte(0) })
            .passthrough(),
          data: z.array(User),
          capabilities: z
            .object({ canPromoteUsers: z.boolean() })
            .passthrough()
            .optional(),
        })
        .passthrough(),
      401: ProblemDetails,
      403: ProblemDetails,
      500: ProblemDetails,
    },
  },
  "~loginDeprecated": {
    method: "POST",
    path: "/login",
    summary:
      "User login and file download originally used by Android app. Use /api/attack-library instead.",
    body: z
      .object({
        username: z.string().optional(),
        password: z.string().optional(),
      })
      .passthrough(),
    contentType: "application/json",
    responses: {
      200: c.otherResponse({
        contentType: "application/octet-stream",
        body: z.custom<File>(),
      }),
      400: ProblemDetails,
      404: c.otherResponse({ contentType: "text/plain", body: z.string() }),
      500: ProblemDetails,
    },
  },
  apiFileDownloadFileName: {
    method: "GET",
    path: "/api/file-download/:fileName",
    summary: "Download a file by file name",
    pathParams: z.object({ fileName: z.string().max(255) }),
    responses: {
      200: c.otherResponse({
        contentType: "application/octet-stream",
        body: z.custom<File>(),
      }),
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      500: ProblemDetails,
    },
  },
  apiFileDownloadUserUserId: {
    method: "GET",
    path: "/api/file-download/user/:userId",
    summary: "Download a file for a user",
    pathParams: z.object({ userId: z.string() }),
    responses: {
      200: c.otherResponse({
        contentType: "application/octet-stream",
        body: z.custom<File>(),
      }),
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      500: ProblemDetails,
    },
  },
  apiLoginUser: {
    method: "POST",
    path: "/api/login-user",
    summary: "User login (returns JWT)",
    body: z
      .object({ email: z.string().min(1), password: z.string().min(1) })
      .passthrough(),
    contentType: "application/json",
    responses: {
      201: LoginUserResponse,
      400: ProblemDetails,
      500: ProblemDetails,
    },
  },
  signOutAllSessions: {
    method: "POST",
    path: "/api/sessions/sign-out-all",
    summary: "Sign out all users (invalidate all active sessions)",
    body: c.noBody(),
    responses: {
      200: z.object({ status: z.literal("ok") }).passthrough(),
      401: ProblemDetails,
      403: ProblemDetails,
      500: ProblemDetails,
    },
  },
  "~apiUserData": {
    method: "POST",
    path: "/api/userData",
    summary: "Get user data from JWT",
    body: c.noBody(),
    responses: { 410: ProblemDetails },
  },
  getUserByUserId: {
    method: "GET",
    path: "/api/users/:userId",
    summary: "Get user data from by userId",
    responses: {
      200: User,
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      500: ProblemDetails,
    },
  },
  deleteUserByUserId: {
    method: "DELETE",
    path: "/api/users/:userId",
    summary: "Delete a user",
    pathParams: z.object({ userId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z
        .object({
          status: z.literal("ok").optional(),
          data: z.literal("Deleted").optional(),
        })
        .passthrough(),
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      409: ProblemDetails,
      500: ProblemDetails,
    },
  },
  promoteUserToAdmin: {
    method: "PUT",
    path: "/api/users/:userId/roles/admin",
    summary: "Promote a user to Admin",
    pathParams: z.object({ userId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({ status: z.literal("ok") }).passthrough(),
      400: ProblemDetails,
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      409: ProblemDetails,
      500: ProblemDetails,
      503: ProblemDetails,
    },
  },
  demoteAdminToUser: {
    method: "DELETE",
    path: "/api/users/:userId/roles/admin",
    summary: "Demote an Admin to User",
    pathParams: z.object({ userId: z.string() }),
    body: c.noBody(),
    responses: {
      200: z.object({ status: z.literal("ok") }).passthrough(),
      400: ProblemDetails,
      401: ProblemDetails,
      403: ProblemDetails,
      404: ProblemDetails,
      409: ProblemDetails,
      500: ProblemDetails,
      503: ProblemDetails,
    },
  },
});
